import React, { useState } from 'react';
import { dismissSuggestion, takeSuggestion } from '../../services/documentNamingService';
import './SuggestedName.css';

/**
 * The line under a document's name once it has been saved (ClickUp
 * 1248h53bmhy, Chris's call 3): "Reading for a name suggestion…", then
 * "Suggested name: … [Use it] [Keep current name]", or the quiet line when no
 * suggestion could be made. Nothing is renamed until Use it is pressed.
 *
 *   <SuggestedName row={nameRow} onChanged={(updatedRow) => ...} />
 *
 * ``row`` is the document's entry from lookupNames; ``onChanged`` gets the
 * server's updated entry after Use it or Keep current name, so the list can
 * show the new name at once.
 */
export default function SuggestedName({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  if (!row || !row.suggestion_status) return null;

  const act = async (action) => {
    setBusy(true);
    setProblem('');
    try {
      const updated = action === 'take'
        ? await takeSuggestion(row.docuware_document_id)
        : await dismissSuggestion(row.docuware_document_id);
      if (onChanged) onChanged(updated);
    } catch (err) {
      setProblem(err?.response?.data?.error || 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (row.suggestion_status === 'reading') {
    return (
      <div className="doc-suggested-name doc-suggested-name--reading" role="status">
        Reading for a name suggestion…
      </div>
    );
  }
  if (row.suggestion_status === 'ready' && row.suggested_display_name) {
    return (
      <div className="doc-suggested-name doc-suggested-name--ready">
        <span className="doc-suggested-name-label">Suggested name:</span>
        <span className="doc-suggested-name-value" title={row.suggested_display_name}>
          {row.suggested_display_name}
        </span>
        <button type="button" className="doc-suggested-name-use" onClick={() => act('take')} disabled={busy}>
          Use it
        </button>
        <button type="button" className="doc-suggested-name-keep" onClick={() => act('dismiss')} disabled={busy}>
          Keep current name
        </button>
        {row.suggestion_message ? (
          <span className="doc-suggested-name-note">{row.suggestion_message}</span>
        ) : null}
        {problem ? <span className="doc-suggested-name-problem" role="alert">{problem}</span> : null}
      </div>
    );
  }
  if (row.suggestion_status === 'failed') {
    return (
      <div className="doc-suggested-name doc-suggested-name--failed">
        {row.suggestion_message || 'No name could be suggested, so the document keeps its name.'}
      </div>
    );
  }
  return null; // taken or kept: nothing more to say
}
